import ResponsiveImage from "@/components/ResponsiveImage";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Layers3, Search, Home, Building2, MapPin } from "lucide-react";
import Seo, { collectionSchema, webPageSchema, breadcrumbSchema, organizationSchema } from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaSection from "@/components/sections/CtaSection";
import { GUIDES } from "@/data/guides";
import { IMAGES } from "@/data/company";

export const GUIDE_VISUALS = {
  "nar-ar-det-dags-att-byta-tak": IMAGES.villaMetal,
  "vad-kostar-ett-takbyte": IMAGES.villaRoofDrone,
  "valja-takmaterial": IMAGES.tileDetail,
  "tecken-pa-takskador": IMAGES.roofDetailWork,
  "takbesiktning-infor-solceller": IMAGES.brfAerial,
  "underhall-av-tak-aret-runt": IMAGES.eavesCraft,
  "takrenovering-for-brf": IMAGES.brfProject,
  "takskottning-ansvar-sno-och-is": IMAGES.snowRemoval,
};

const CLUSTERS = [
  { icon: Home, label: "Takbyte & pris", text: "Beslut, kostnadsdrivare och när ett byte faktiskt blir relevant.", slugs: ["nar-ar-det-dags-att-byta-tak", "vad-kostar-ett-takbyte"], to: "/byta-tak/" },
  { icon: Layers3, label: "Material", text: "Plåt, papp, tegel och betong – utifrån takets konstruktion.", slugs: ["valja-takmaterial"], to: "/takmaterial/" },
  { icon: Search, label: "Skador & besiktning", text: "Tecken att reagera på och underlag inför nästa åtgärd.", slugs: ["tecken-pa-takskador", "takbesiktning-infor-solceller"], to: "/takbesiktning/" },
  { icon: Building2, label: "BRF & underhåll", text: "Styrelseunderlag, service, vinter och ansvar över tid.", slugs: ["takrenovering-for-brf", "underhall-av-tak-aret-runt", "takskottning-ansvar-sno-och-is"], to: "/brf-foretag/" },
];

export default function Kunskapsbank() {
  const path = "/kunskapsbank/";
  const crumbs = [{ name: "Råd & guider", path }];
  const featured = GUIDES[0];
  const remaining = GUIDES.slice(1);
  const schema = [organizationSchema(), webPageSchema(path, "Råd & guider – TISAB", "Beslutsstöd om takbyte, material, skador, besiktning, BRF och underhåll i Stockholm."), breadcrumbSchema(path, crumbs),  collectionSchema(path, "Innehåll", GUIDES.map(guide => ({ name: guide.h1, path: `/kunskapsbank/${guide.slug}/` })))
];

  return (
    <>
      <Seo title="Takguider – pris, material & underhåll | TISAB" description="När behöver taket bytas och vad påverkar kostnaden? Läs TISAB:s guider om takbyte, material, skador och underhåll. Förbered nästa beslut om ditt tak." path={path} schema={schema} image={GUIDE_VISUALS[featured.slug]} />
      <Breadcrumbs items={crumbs} />

      <section className="bg-parchment">
        <div className="container-tisab grid gap-8 py-12 md:py-16 lg:grid-cols-[.64fr_.36fr] lg:items-stretch">
          <Link to={`/kunskapsbank/${featured.slug}/`} className="group relative min-h-[500px] overflow-hidden bg-midnight text-white sm:min-h-[590px]">
            <ResponsiveImage src={GUIDE_VISUALS[featured.slug]} alt={featured.h1} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" fetchPriority="high" />
            <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/46 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7 sm:p-10">
              <span className="inline-flex items-center gap-2 border border-white/20 bg-midnight/48 px-3 py-2 text-[10px] font-bold uppercase tracking-[.16em] text-[#0a965c]"><Clock className="h-3.5 w-3.5" /> Läs först</span>
              <h2 className="mt-5 max-w-3xl font-heading text-4xl font-extrabold leading-[.96] tracking-[-.035em] sm:text-6xl">{featured.h1}</h2>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-white/72">{featured.intro}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#0a965c]">Läs guiden <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </div>
          </Link>

          <div className="flex flex-col border border-midnight/10 bg-white p-7 sm:p-9">
            <p className="eyebrow">TISAB:s råd & guider</p>
            <h1 className="mt-3 font-heading text-4xl font-extrabold leading-[.97] text-midnight">Råd och guider om tak</h1>
            <p className="mt-5 text-sm leading-relaxed text-foreground/66">Här hittar du råd om att laga eller byta tak, jämföra offerter, välja material och planera underhåll. Börja med den fråga som gäller ditt tak.</p>
            <div className="mt-8 grid gap-px border border-midnight/10 bg-midnight/10">
              {CLUSTERS.map((cluster) => (
                <a key={cluster.label} href={`#${cluster.label.toLowerCase().replaceAll(" ", "-").replace("&", "och")}`} className="group flex items-center justify-between gap-4 bg-parchment p-4 hover:bg-[#e8f3ed]">
                  <span className="flex items-center gap-3"><cluster.icon className="h-5 w-5 text-tisab" /><span className="font-heading font-extrabold text-midnight">{cluster.label}</span></span>
                  <ArrowRight className="h-4 w-4 text-tisab transition-transform group-hover:translate-x-1" />
                </a>
              ))}
            </div>
            <div className="mt-auto pt-8">
              <Link to="/kontakta-oss/" className="btn-primary w-full">Fråga TISAB om ditt tak <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-midnight/10 bg-[#edf3ef] py-12 sm:py-16">
        <div className="container-tisab">
          <p className="eyebrow">Ämnen som hänger ihop</p>
          <div className="mt-7 grid gap-px border border-midnight/10 bg-midnight/10 md:grid-cols-2 lg:grid-cols-4">
            {CLUSTERS.map((cluster) => (
              <article key={cluster.label} id={cluster.label.toLowerCase().replaceAll(" ", "-").replace("&", "och")} className="bg-white p-6 sm:p-7">
                <cluster.icon className="h-6 w-6 text-tisab" />
                <h2 className="mt-5 font-heading text-2xl font-extrabold text-midnight">{cluster.label}</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground/70">{cluster.text}</p>
                <div className="mt-5 space-y-2">
                  {cluster.slugs.map((slug) => {
                    const guide = GUIDES.find((item) => item.slug === slug);
                    return guide ? <Link key={slug} to={`/kunskapsbank/${slug}/`} className="flex items-start gap-2 text-xs font-semibold text-foreground/70 hover:text-tisab"><ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tisab" /> {guide.h1}</Link> : null;
                  })}
                </div>
                <Link to={cluster.to} className="mt-6 inline-flex items-center gap-2 text-xs font-bold text-tisab">Relaterad tjänst <ArrowRight className="h-3.5 w-3.5" /></Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-parchment">
        <div className="container-tisab">
          <div className="grid gap-5 lg:grid-cols-[.7fr_.3fr] lg:items-end">
            <div>
              <p className="eyebrow">Fördjupning</p>
              <h2 className="mt-3 font-heading text-4xl font-extrabold leading-[.96] text-midnight sm:text-6xl">Guider byggda kring verkliga beslut.</h2>
            </div>
            <p className="border-l-2 border-tisab pl-5 text-sm leading-relaxed text-foreground/70">Använd guiderna för att förstå alternativen, samla rätt frågor och förbereda en bedömning av din fastighet.</p>
          </div>
          <div className="mt-10 grid auto-rows-[220px] gap-4 md:grid-cols-2 lg:grid-cols-12">
            {remaining.map((guide, index) => (
              <Link key={guide.slug} to={`/kunskapsbank/${guide.slug}/`} className={`group relative overflow-hidden bg-midnight text-white ${index === 0 || index === 4 ? "lg:col-span-7 lg:row-span-2" : "lg:col-span-5"}`}>
                <ResponsiveImage src={GUIDE_VISUALS[guide.slug] || guide.image} alt={guide.h1} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.035]" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/38 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7">
                  <span className="text-[9px] font-bold uppercase tracking-[.16em] text-[#0a965c]">Guide {String(index + 2).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-heading text-2xl font-extrabold leading-tight sm:text-3xl">{guide.h1}</h3>
                  <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-white/66">{guide.intro}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-[#0a965c]">Läs hela guiden <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-midnight/10 bg-white py-14 sm:py-18">
        <div className="container-tisab grid gap-7 lg:grid-cols-[.38fr_.62fr] lg:items-center">
          <div>
            <p className="eyebrow">Lokalt nästa steg</p>
            <h2 className="mt-3 font-heading text-3xl font-extrabold text-midnight sm:text-4xl">Från kunskap till hjälp nära taket.</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {["stockholm", "lidingo", "nacka", "solna", "danderyd", "taby"].map((slug) => (
              <Link key={slug} to={`/taklaggare/${slug}/`} className="inline-flex min-h-11 items-center gap-2 border border-midnight/12 bg-parchment px-4 text-sm font-semibold capitalize text-midnight hover:border-tisab hover:text-tisab"><MapPin className="h-3.5 w-3.5" /> {slug}</Link>
            ))}
            <Link to="/omraden/" className="inline-flex min-h-11 items-center gap-2 bg-midnight px-4 text-sm font-bold text-white">Alla områden <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <CtaSection title="Fastnat mellan två alternativ?" text="Beskriv taket och frågan. TISAB hjälper dig avgöra om nästa steg är besiktning, service, renovering eller offert." />
    </>
  );
}